<template>
  <modal
    v-if="isPrimary"
    :disabled="false"
    :modal-heading="modalHeading"
    :modal-open="modalIsOpen"
    :toggle-modal="toggleModal"
    class="review-modal"
  >
    <p class="review-modal__body" v-text="modalReview.review" />

    <p
      v-if="customerName || showDate"
      class="review-modal__meta"
    >
      <span v-if="customerName" class="review-modal__name" v-text="customerName" />
      <span
        v-if="customerName && showDate"
        class="review-modal__meta-divider"
        aria-hidden="true"
        >·</span
      >
      <span
        v-if="showDate"
        class="review-modal__time"
        v-text="timeSinceDate(modalReview.review_date)"
      />
    </p>

    <div v-if="modalReview.reply" class="review-modal__reply">
      <p v-text="texts.replyFromLavendla" />
      <p v-text="modalReview.reply" />
    </div>

    <div
      v-if="!modalReview.custom || platformLabel"
      class="review-modal__footer"
    >
      <review-stars
        v-if="!modalReview.custom"
        class="review-modal__rating"
        :count="starCount"
        :size="20"
        :spacing="3"
        :aria-label="getStarLabel(modalReview.rating)"
      />

      <div
        v-if="platformLabel"
        class="review-modal__platform"
      >
        <span class="review-modal__platform-prefix">{{ texts.reviewFrom }}</span>
        <platform-glyph :platform="platform" />
      </div>
    </div>
  </modal>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import Modal from 'Components/Modal.vue';
import { useDateFormatter } from 'Composables/useDateFormatter';
import { getStarLabel } from '../misc/helpers';
import ReviewStars from './ReviewStars.vue';
import PlatformGlyph from './PlatformGlyph.vue';

const PLATFORM_LABELS = {
  trustpilot: 'Trustpilot',
  google: 'Google',
  reco: 'Reco',
};

// Multiple Vue apps (e.g., CounselorReviews + footer ReviewsCarousel) can each
// render a ReviewModal off the shared Vuex modalReview state — claim a single
// owner so only one modal renders at a time.
let activeInstance = null;

export default {
  name: 'ReviewModal',

  components: {
    Modal,
    ReviewStars,
    PlatformGlyph,
  },

  setup() {
    const { timeSinceDate } = useDateFormatter();
    return { timeSinceDate };
  },

  data() {
    return {
      texts: window.theme_vars.trustpilot,
      hideReviewDate: !!window.theme_vars.hideReviewDate,
      isPrimary: false,
    };
  },

  created() {
    if (!activeInstance) {
      activeInstance = this;
      this.isPrimary = true;
    }
  },

  beforeUnmount() {
    if (activeInstance === this) {
      activeInstance = null;
    }
  },

  computed: {
    ...mapState('reviews', ['modalIsOpen', 'modalReview']),

    platform() {
      // API exposes the platform on `source`. Older callers may still pass
      // `platform`, so honor both. Falls back to Trustpilot if missing.
      const value =
        (this.modalReview && (this.modalReview.source || this.modalReview.platform)) ||
        'trustpilot';
      return PLATFORM_LABELS[value] ? value : 'trustpilot';
    },

    platformLabel() {
      if (this.modalReview && this.modalReview.custom) {
        return '';
      }
      return PLATFORM_LABELS[this.platform];
    },

    customerName() {
      return (this.modalReview && (this.modalReview.customer || this.modalReview.customer_name)) || '';
    },

    showDate() {
      return !this.hideReviewDate && !!(this.modalReview && this.modalReview.review_date);
    },

    starCount() {
      const rating = Number(this.modalReview && this.modalReview.rating);
      return Math.round(Number.isFinite(rating) ? rating : 5);
    },

    modalHeading() {
      const title = (this.modalReview && this.modalReview.title) || '';
      if (title) {
        return title;
      }
      if (this.platformLabel && this.texts.reviewFrom) {
        return `${this.texts.reviewFrom} ${this.platformLabel}`;
      }
      return this.texts.reviewFrom || 'Review';
    },
  },

  methods: {
    ...mapActions('reviews', ['openModal', 'closeModal']),

    getStarLabel,

    toggleModal() {
      if (this.modalIsOpen) {
        this.closeModal();
      } else {
        this.openModal(this.modalReview);
      }
    },
  },
};
</script>
